import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';

// Currency formatter that honours the property's currency_code.
function fmtMoney(value, code = 'INR') {
  const n = Number(value || 0);
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency', currency: code, maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `${code} ${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  }
}

function fmtPct(value) {
  if (value === null || value === undefined) return '—';
  return `${Number(value).toFixed(1)}%`;
}

const CARD = {
  background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
  padding: '14px 16px', boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
};

function Widget({ label, value, sub, accent, hint }) {
  return (
    <div style={{ ...CARD, minHeight: 110 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280',
        textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, color: accent || '#0f172a', marginTop: 6 }}>
        {value}
      </div>
      {sub ? <div style={{ fontSize: 12, color: '#475569', marginTop: 4 }}>{sub}</div> : null}
      {hint ? <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>{hint}</div> : null}
    </div>
  );
}

function PmsRevenueTargetDashboard({ token, selectedProperty, permissions }) {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [trendDaily, setTrendDaily] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showBreakdown, setShowBreakdown] = useState(true);

  const canAdmin = Boolean(permissions?.revenue_target_admin || permissions?.hotel_admin);

  const authHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
  }), [token, selectedProperty]);

  const load = useCallback(async () => {
    if (!selectedProperty?.id) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`/api/v1/revenue-target/summary?date=${date}`, { headers: authHeaders() });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error || 'Failed to load');
      setData(body);
    } catch (err) {
      setError(err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [selectedProperty, date, authHeaders]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 60s while open
  useEffect(() => {
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  // Daily trend for current month — fetched in parallel
  useEffect(() => {
    if (!selectedProperty?.id || !data?.business_date) return;
    const ym = data.business_date.slice(0, 7);
    const monthStart = `${ym}-01`;
    (async () => {
      // Fetch summaries for each day in the month so far. For 31 days we cap
      // at the current business_date — past-future dates show zero revenue.
      // This is a simple per-day fetch; will become a dedicated endpoint in
      // Phase 2 alongside the reports.
      const today = data.business_date;
      const days = Number(today.slice(8, 10));
      const series = [];
      for (let d = 1; d <= days; d += 1) {
        const dStr = `${ym}-${String(d).padStart(2, '0')}`;
        try {
          const r = await fetch(`/api/v1/revenue-target/summary?date=${dStr}`, { headers: authHeaders() });
          const j = await r.json();
          if (r.ok) series.push({
            day: d,
            revenue: Number(j?.revenue?.today?.total || 0),
            target_pace: data?.target?.target_amount && data?.achievement?.days_in_month
              ? Number(data.target.target_amount) / Number(data.achievement.days_in_month)
              : 0,
          });
        } catch {}
        if (d > 7 && series.length === 0) break; // bail if the API is failing repeatedly
      }
      setTrendDaily(series);
    })();
  // We deliberately depend only on the month-key + business_date to avoid
  // re-fetching the whole month on each 60s refresh of the summary.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProperty?.id, data?.business_date?.slice(0, 7)]);

  const currency = data?.currency_code || 'INR';
  const ach = data?.achievement || {};
  const today = data?.revenue?.today || {};
  const mtd = data?.revenue?.mtd || {};
  const occ = data?.occupancy || {};
  const tgt = data?.target || {};
  const exp = data?.expenses || {};
  const additional = data?.revenue?.additional_breakdown || [];

  const paceColor = useMemo(() => {
    const lbl = ach?.pace?.label || '';
    if (lbl === 'Ahead') return '#16a34a';
    if (lbl === 'Behind') return '#dc2626';
    if (lbl === 'No target set') return '#94a3b8';
    return '#0f172a';
  }, [ach?.pace?.label]);

  if (!selectedProperty?.id) {
    return (
      <div style={{ padding: 24 }}>
        <h3>Revenue vs Monthly Target</h3>
        <div className="error-banner">Please select a property first.</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '0.75rem 1rem', fontFamily: 'inherit' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 11, color: '#6b7280' }}>
            Expense &amp; Profitability <span style={{ color: '#cbd5e0' }}>›</span>{' '}
            <span style={{ color: 'var(--theme-primary)', fontWeight: 600 }}>Revenue vs Target</span>
          </div>
          <h3 style={{ margin: '4px 0 0', color: 'var(--theme-primary)' }}>
            Revenue vs Monthly Target — {data?.month_label || ''}
          </h3>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input type="date" value={date} max={new Date().toISOString().slice(0, 10)}
            onChange={(e) => setDate(e.target.value)}
            style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }} />
          <button onClick={load} disabled={loading}
            style={{ padding: '6px 14px', background: '#2563eb', color: '#fff', border: 'none',
              borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && <div className="error-banner" style={{ marginBottom: 12 }}>{error}</div>}

      {!data && !loading && !error && (
        <div style={{ padding: 24, color: '#6b7280' }}>No data.</div>
      )}

      {data && (
        <>
          {!tgt.is_set && (
            <div style={{ padding: '10px 14px', background: '#fffbeb', border: '1px solid #fbbf24',
              borderRadius: 8, color: '#92400e', fontSize: 13, marginBottom: 12 }}>
              No monthly target is set for {data.month_label}.{' '}
              {canAdmin
                ? <>Set one under <b>Expense &amp; Profitability → Masters → Revenue Targets</b>.</>
                : <>Ask an admin to configure it.</>}
            </div>
          )}

          {/* 7-widget grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 14 }}>
            <Widget
              label="Monthly Target"
              value={tgt.is_set ? fmtMoney(tgt.target_amount, currency) : '—'}
              sub={!tgt.is_set ? 'Not set' : null}
            />
            <Widget
              label="Revenue Achieved (MTD)"
              value={fmtMoney(mtd.total, currency)}
              sub={tgt.is_set ? `of ${fmtMoney(tgt.target_amount, currency)} target` : null}
              accent="#0f172a"
            />
            <Widget
              label="Remaining Target"
              value={tgt.is_set ? fmtMoney(ach.remaining_target, currency) : '—'}
              sub={tgt.is_set ? `${ach.days_in_month - ach.days_elapsed} day(s) left` : null}
              accent={ach.remaining_target === 0 ? '#16a34a' : '#0f172a'}
            />
            <Widget
              label="Daily Revenue (Today)"
              value={fmtMoney(today.total, currency)}
              sub={`Room ${fmtMoney(today.room, currency)} · POS ${fmtMoney((today.pos_fnb || 0) + (today.pos_bar || 0), currency)} · Addl ${fmtMoney(today.additional, currency)}`}
            />
            <Widget
              label="Occupancy %"
              value={fmtPct(occ.occupancy_pct)}
              sub={`${occ.occupied_rooms || 0} / ${occ.sellable_rooms || 0} rooms`}
            />
            <Widget
              label="Additional Revenue (Today)"
              value={fmtMoney(today.additional, currency)}
              sub="Laundry · Room Service · Misc"
            />
            <Widget
              label="Achievement %"
              value={ach.achievement_pct === null ? '—' : fmtPct(ach.achievement_pct)}
              sub={ach.pace?.label}
              accent={paceColor}
              hint={ach.pace?.delta_pct ? `${ach.pace.delta_pct > 0 ? '+' : ''}${ach.pace.delta_pct}% vs expected pace` : null}
            />
          </div>

          {/* Achievement progress bar */}
          {tgt.is_set && (
            <div style={{ ...CARD, marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 12, color: '#475569' }}>
                <span>{fmtMoney(0, currency)}</span>
                <span style={{ fontWeight: 700, color: paceColor }}>
                  {fmtMoney(mtd.total, currency)} achieved ({fmtPct(ach.achievement_pct)})
                </span>
                <span>{fmtMoney(tgt.target_amount, currency)}</span>
              </div>
              <div style={{ height: 14, background: '#f1f5f9', borderRadius: 7, overflow: 'hidden', position: 'relative' }}>
                <div style={{
                  width: `${Math.min(100, Number(ach.achievement_pct || 0))}%`,
                  height: '100%',
                  background: ach.achievement_pct >= 100 ? '#16a34a' : (ach.achievement_pct >= 80 ? '#2563eb' : '#f59e0b'),
                  transition: 'width 600ms',
                }} />
                {/* Pace marker */}
                {ach.pace?.expected_mtd > 0 && tgt.target_amount > 0 && (
                  <div title={`Expected pace: ${fmtMoney(ach.pace.expected_mtd, currency)}`}
                    style={{
                      position: 'absolute', top: -2, bottom: -2,
                      left: `${Math.min(100, (Number(ach.pace.expected_mtd) / Number(tgt.target_amount)) * 100)}%`,
                      width: 2, background: '#0f172a',
                    }} />
                )}
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                Vertical line = expected pace ({fmtMoney(ach.pace?.expected_mtd, currency)} by day {ach.days_elapsed} of {ach.days_in_month})
              </div>
            </div>
          )}

          {/* Expense overlay strip */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 14 }}>
            <Widget label="Budgeted Expenses (Month)" value={fmtMoney(exp.budgeted_this_month, currency)} sub="From Expense Module" />
            <Widget label="Expenses Incurred MTD" value={fmtMoney(exp.incurred_mtd, currency)} sub="Approved + Posted" />
            <Widget label="Forecast Profit" value={fmtMoney(exp.forecast_profit, currency)}
              accent={exp.forecast_profit >= 0 ? '#16a34a' : '#dc2626'}
              sub="Target − Budgeted" />
            <Widget label="Actual Profit MTD" value={fmtMoney(exp.actual_profit_mtd, currency)}
              accent={exp.actual_profit_mtd >= 0 ? '#16a34a' : '#dc2626'}
              sub="Revenue − Expenses" />
          </div>

          {/* Daily trend chart */}
          {trendDaily.length > 1 && (
            <div style={{ ...CARD, marginBottom: 14 }}>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Daily Revenue vs Required Pace — {data.month_label}</div>
              <div style={{ width: '100%', height: 240 }}>
                <ResponsiveContainer>
                  <LineChart data={trendDaily} margin={{ top: 5, right: 14, bottom: 5, left: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => fmtMoney(v, currency).replace(currency, '').trim()} />
                    <Tooltip formatter={(v) => fmtMoney(v, currency)} />
                    <Legend />
                    <Line type="monotone" dataKey="revenue" stroke="#2563eb" strokeWidth={2} name="Actual" />
                    {tgt.is_set && (
                      <Line type="monotone" dataKey="target_pace" stroke="#94a3b8" strokeDasharray="4 4" strokeWidth={2} name="Required daily" />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Additional revenue breakdown */}
          <div style={CARD}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>Additional Revenue Breakdown (MTD)</div>
              <button onClick={() => setShowBreakdown(s => !s)}
                style={{ background: 'transparent', border: 'none', color: 'var(--theme-primary)', cursor: 'pointer', fontSize: 12 }}>
                {showBreakdown ? 'Hide' : 'Show'}
              </button>
            </div>
            {showBreakdown && (
              additional.length === 0
                ? <div style={{ fontSize: 13, color: '#6b7280' }}>No additional revenue this month yet.</div>
                : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: '#f9fafb' }}>
                        <th style={{ padding: '6px 10px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Category</th>
                        <th style={{ padding: '6px 10px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {additional.map((r) => (
                        <tr key={r.category} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '6px 10px' }}>{r.category.replace(/_/g, ' ')}</td>
                          <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'monospace' }}>{fmtMoney(r.amount, currency)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )
            )}
          </div>

          <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 10 }}>
            View mode: {data.view_mode} · Currency: {currency} · Auto-refresh every 60s
          </div>
        </>
      )}
    </div>
  );
}

export default PmsRevenueTargetDashboard;
